import React from 'react'
import PropTypes from 'prop-types'
import {Box, Image, Flex, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {convertSfccHrefToPwaHref} from '@consteed/frontend/utils/richTextConfigurations'
import Link from '@consteed/overrides/components/link'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui/Link'
import {NavigateNextIcon} from '@consteed/overrides/components/icons'

// @todo: add responsive behaviour/image-sizes
const Picture = ({image, alt}) => (
    <picture>
        <source srcSet={image?.src?.tablet} media="(min-width: 48em)" />
        <source srcSet={image?.src?.desktop} media="(min-width: 64em)" />
        <Image
            data-testid={'imageTextButton-image'}
            src={image?.src?.mobile ? image?.src?.mobile : image?.url}
            ignoreFallback={true}
            alt={alt}
            title={alt}
        />
    </picture>
)
Picture.propTypes = {
    image: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    alt: PropTypes.string
}

/**
 * ImageTextButton component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Button Link.
 * @param {string} - props.ITCText - Text beside Image.
 * @param {image} - props.image - Image.
 * @param {string} - props.alt - The image alt text shown by the component.
 * @returns {React.ReactElement} - ImageTextButton component.
 */
const ImageTextButton = ({ITCLink, ITCText, image, alt, buttonText}) => {
    const pwaITCLink = convertSfccHrefToPwaHref(ITCLink)
    const isAbsolute = isAbsoluteURL(pwaITCLink)
    const LinkWrapper = isAbsolute ? ChakraLink : Link
    const linkProps = isAbsolute ? {href: pwaITCLink} : {to: pwaITCLink}

    return (
        <Box className={'pdcOuterBox'}>
            <Flex direction={{base: 'column', md: 'row'}} align="center" gap={4} className={'flex'}>
                <Box flex="1" mb={{base: 4, md: 0}}>
                    {ITCText && <RTE richText={ITCText} />}
                    {ITCLink && buttonText && (
                        <Button
                            as={Link}
                            href={ITCLink}
                            variant={'primary'}
                            size={'md'}
                            rightIcon={<NavigateNextIcon />}
                            width={{base: 'full', md: 'inherit'}}
                            mt={5}
                        >
                            {buttonText}
                        </Button>
                    )}
                </Box>
                <Box flex="1">
                    {ITCLink && (
                        <LinkWrapper {...linkProps}>
                            <Picture image={image} alt={alt} />
                        </LinkWrapper>
                    )}
                    {!ITCLink && <Picture image={image} alt={alt} />}
                </Box>
            </Flex>
        </Box>
    )
}

ImageTextButton.propTypes = {
    ITCLink: PropTypes.string,
    ITCText: PropTypes.string,
    image: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    alt: PropTypes.string,
    buttonText: PropTypes.string
}

export default ImageTextButton
